import React, { useState, useEffect } from 'react';
import {
  Container, Paper, Box, Typography, Avatar, Grid, Divider,
  CircularProgress, Alert, Chip
} from '@mui/material';
import { Badge, Email, Phone, LocationOn, Shield } from '@mui/icons-material';
import { useUser } from "../../Context/UserContext";
import { GetApi } from "../../Services/api/ApiCall";
import API_URLS from "../../Services/api/action";

const Profile = () => {
  const { authData, user, token } = useUser();
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        
        // Check if user is an officer/superadmin
        const userRole = (authData?.role || '').toLowerCase();
        const isOfficer = ['officer', 'superadmin', 'admin', 'block_admin', 'district_admin']
          .some(role => userRole.includes(role));

        let response;
        if (isOfficer) {
          response = await GetApi(`${API_URLS.GetofficerProfile}`, token);
        } else {
          response = await GetApi(`${API_URLS.GetUserProfile}`, token);
        }

        setProfileData(response);
      } catch (err) {
        console.error("Failed to fetch profile:", err);
        setError("Failed to load profile details. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchProfile();
    }
  }, [token, authData?.role]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress sx={{ color: '#13d813' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 5 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  const displayData = profileData || user || {};

  // Determine display values - handles both user and officer responses
  const fullName = displayData?.full_name || authData?.user_name || 'N/A';
  const email = displayData?.email || 'Not Provided';
  const phone = displayData?.mobile_number || 'Not Provided';
  const location = displayData?.location || displayData?.village || 'Not Provided';
  const avatarLetter = fullName !== 'N/A' ? fullName.charAt(0).toUpperCase() : 'U';

  const profileFields = [
    {
      icon: <Badge sx={{ color: '#005500', fontSize: '1.2rem' }} />,
      label: 'Full Name',
      value: fullName
    },
    {
      icon: <Email sx={{ color: '#005500', fontSize: '1.2rem' }} />,
      label: 'Email Address',
      value: email
    },
    {
      icon: <Phone sx={{ color: '#005500', fontSize: '1.2rem' }} />,
      label: 'Phone Number',
      value: phone
    },
    {
      icon: <LocationOn sx={{ color: '#005500', fontSize: '1.2rem' }} />,
      label: 'Location',
      value: location
    }
  ];

  const colors = {
    primary: '#000000',
    success: '#005500',
    accent: '#ffc022'
  };

  return (
    <Container maxWidth="md" sx={{ mt: 5, mb: 5 }}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: `1px solid ${colors.primary}20`,
          overflow: 'hidden',
          background: '#f8f9fa'
        }}
      >
        {/* Header Banner */}
        <Box
          sx={{
            height: 160,
            background: `linear-gradient(135deg, #209b20 0%, #128112 50%, #047204 100%)`,
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'radial-gradient(circle at 20% 50%, rgba(255, 192, 34, 0.08) 0%, transparent 50%)',
            }
          }}
        >
          {/* Subtle pattern overlay */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              opacity: 0.03,
              backgroundImage: `repeating-linear-gradient(
                45deg,
                transparent,
                transparent 35px,
                rgba(255,255,255,0.5) 35px,
                rgba(255,255,255,0.5) 36px
              )`,
            }}
          />
          
          {/* Avatar Container */}
          <Box
            sx={{
              position: 'absolute',
              bottom: -45,
              left: 40,
              zIndex: 2
            }}
          >
            <Avatar
              sx={{
                width: 110,
                height: 110,
                bgcolor: colors.accent,
                color: colors.success,
                fontSize: '2.8rem',
                fontWeight: 700,
                border: `4px solid #f8f9fa`,
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
              }}
            >
              {avatarLetter}
            </Avatar>
          </Box>
        </Box>

        {/* Content Area */}
        <Box sx={{ pt: 8, pr: 5, pb: 5, pl: 5 }}>
          {/* Name & Role Section */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h4"
              fontWeight={700}
              sx={{
                color: colors.primary,
                letterSpacing: '-0.5px'
              }}
            >
              {fullName}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1 }}>
              <Shield sx={{ color: colors.success, fontSize: '1rem' }} />
              <Chip
                label={displayData?.role || authData?.role || 'User'}
                size="small"
                sx={{
                  bgcolor: `${colors.success}12`,
                  color: colors.success,
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  border: `1px solid ${colors.success}30`,
                  borderRadius: 2
                }}
              />
            </Box>
          </Box>

          <Divider sx={{ borderColor: `${colors.primary}15`, mb: 4 }} />

          {/* Profile Info Grid */}
          <Grid container spacing={3}>
            {profileFields.map((field, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2.5,
                    p: 3,
                    bgcolor: '#ffffff',
                    borderRadius: 3,
                    border: `1px solid ${colors.primary}10`,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: `${colors.success}40`,
                      boxShadow: '0 2px 12px rgba(0, 85, 0, 0.08)',
                      transform: 'translateY(-1px)'
                    }
                  }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2.5,
                      bgcolor: `${colors.success}10`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}
                  >
                    {field.icon}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: `${colors.primary}60`,
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}
                    >
                      {field.label}
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight={600}
                      sx={{
                        color: field.value === 'Not Provided' || field.value === 'N/A'
                          ? `${colors.primary}40`
                          : colors.primary,
                        mt: 0.5,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {field.value}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>

          {/* Footer Info */}
          <Box
            sx={{
              mt: 4,
              pt: 3,
              borderTop: `1px solid ${colors.primary}10`,
              display: 'flex',
              justifyContent: 'center'
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: `${colors.primary}50`,
                fontSize: '0.8rem'
              }}
            >
              Profile information synced from server
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default Profile;